import React, { useMemo, useState } from 'react';
import { ChevronRight } from 'lucide-react';

export const ACTION_LABELS = {
  create: 'Create new',
  match_existing: 'Match existing',
  replace: 'Replace previous',
};

const ACTION_BADGES = {
  create: 'badge-success',
  match_existing: 'badge-info',
  replace: 'badge-warning',
};

const groupBy = (rows, keyFn) => {
  const out = {};
  (rows || []).forEach((row) => {
    const key = keyFn(row);
    if (!key) return;
    (out[key] = out[key] || []).push(row);
  });
  return out;
};

const Chip = ({ children }) => (
  <span className="badge badge-gray tabular-nums">
    {children}
  </span>
);

const ActionBadge = ({ action }) =>
  action ? (
    <span className={`badge badge-dot ${ACTION_BADGES[action] || ACTION_BADGES.match_existing}`}>
      {ACTION_LABELS[action] || action}
    </span>
  ) : null;

const Row = ({ primary, chips = [], detail }) => (
  <li className="flex items-start justify-between gap-3 py-1.5">
    <div className="min-w-0">
      <p className="truncate text-[13px] text-gray-900 dark:text-gray-50">{primary}</p>
      {detail && <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{detail}</p>}
    </div>
    <div className="flex gap-1 shrink-0">{chips.map((c, i) => <Chip key={i}>{c}</Chip>)}</div>
  </li>
);

const PlannedChangesReview = ({ payload, plannedActions = {}, classification }) => {
  const [open, setOpen] = useState(() => new Set());

  const toggle = (key) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const model = useMemo(() => {
    if (!payload) return null;
    const refMap = {};
    Object.values(payload).forEach((rows) =>
      (rows || []).forEach((row) => {
        if (row?.ref) refMap[row.ref] = row;
      })
    );
    const screenQuestionsByScreen = groupBy(payload.screen_question, (sq) => sq.screen);
    const navRulesByScreen = groupBy(
      payload.navigation_rule,
      (r) => refMap[r.screen_question]?.screen
    );
    return {
      refMap,
      assessmentsBySurvey: groupBy(payload.assessment, (a) => a.survey),
      questionsByAssessment: groupBy(payload.question, (q) => q.assessment),
      questionsBySurvey: groupBy(payload.question, (q) => q.survey),
      screensBySurvey: groupBy(payload.screen, (s) => s.survey),
      screenQuestionsByScreen,
      navRulesByScreen,
      scoringByAssessment: groupBy(payload.scoring_rule, (r) => r.assessment),
      versionsBySchedule: groupBy(payload.schedule_version, (v) => v.schedule),
    };
  }, [payload]);

  if (!payload || !model) return null;

  const { refMap } = model;
  const study = (payload.study || [])[0];
  const mfa = (payload.mfa_settings || [])[0];
  const surveys = payload.survey || [];
  const templates = [
    ...(payload.layout_template || []).map((t) => ({ ...t, _kind: 'Question layout' })),
    ...(payload.screen_layout_template || []).map((t) => ({ ...t, _kind: 'Screen layout' })),
    ...(payload.assessment_template || []).map((t) => ({ ...t, _kind: 'Assessment template' })),
  ];
  const variants = payload.form_variant || [];
  const schedules = payload.schedule || [];
  const reminders = payload.reminder_template || [];
  const references = [
    ...(payload.device || []).map((d) => ({ label: d.name, kind: 'Device', extra: d.device_type })),
    ...(payload.screen_template || []).map((t) => ({ label: t.name, kind: 'Screen template' })),
    ...(payload.vpg_question || []).map((q) => ({ label: q.text_preview || q.code, kind: 'VPG question' })),
  ];

  const Section = ({ id, title, count, action, children, note }) => (
    <div className="border-b border-gray-100 last:border-b-0 dark:border-white/[0.06]">
      <button
        onClick={() => toggle(id)}
        className="flex w-full items-center gap-3 px-5 py-3 text-left transition-colors hover:bg-gray-50/80 dark:hover:bg-white/[0.03]"
      >
        <ChevronRight
          className={`w-4 h-4 text-gray-400 shrink-0 transition-transform ${open.has(id) ? 'rotate-90' : ''}`}
        />
        <span className="flex-1 text-[13.5px] font-medium text-gray-900 dark:text-gray-50">
          {title}
        </span>
        {note && <span className="text-xs text-gray-500 dark:text-gray-400 hidden sm:block">{note}</span>}
        {count != null && <Chip>{count}</Chip>}
        <ActionBadge action={action} />
      </button>
      {open.has(id) && <div className="pb-3 pl-12 pr-5">{children}</div>}
    </div>
  );

  const SubSection = ({ id, title, count, children }) => (
    <div className="mt-1">
      <button
        onClick={() => toggle(id)}
        className="flex items-center gap-2 py-1 text-left text-xs font-medium text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-gray-100"
      >
        <ChevronRight
          className={`w-3 h-3 text-gray-400 transition-transform ${open.has(id) ? 'rotate-90' : ''}`}
        />
        {title}
        <Chip>{count}</Chip>
      </button>
      {open.has(id) && (
        <ul className="ml-5 divide-y divide-gray-100 dark:divide-white/[0.06]">{children}</ul>
      )}
    </div>
  );

  const settingRows = study
    ? [
        ['Study code', study.code],
        ['Title', study.title],
        ['Description', study.description],
        ['Device strategy', study.device_strategy],
        ['BYOD allowed', study.is_byod_allowed ? 'yes' : 'no'],
        ['Caregivers allowed', study.allow_caregivers ? 'yes' : 'no'],
        ['Enrollment without schedule', study.allow_enrollment_without_schedule ? 'yes' : 'no'],
        ['Participant portal title', study.participant_portal_title],
        ['Provisioned devices', (study.provisioned_devices || []).map((r) => refMap[r]?.name).filter(Boolean).join(', ')],
        mfa && ['MFA required for all users', mfa.mfa_required_for_all_users ? `yes (grace ${mfa.grace_period_days} days)` : 'no'],
      ].filter((row) => row && row[1] != null && row[1] !== '')
    : [];

  return (
    <div className="card overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 px-5 py-4 dark:border-white/[0.06]">
        <h3 className="text-[15px] font-semibold text-gray-900 dark:text-gray-50">
          Planned changes
        </h3>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {classification === 're_promotion'
            ? 'Re-promotion: the previous deployment’s content below is replaced in one transaction.'
            : 'Everything below is created new in one transaction.'}
        </p>
      </div>

      <Section id="study" title="Study record & settings" count={settingRows.length} action={plannedActions.study}>
        <dl className="text-[13px]">
          {settingRows.map(([label, value]) => (
            <div key={label} className="flex gap-3 py-1">
              <dt className="w-56 shrink-0 text-gray-500 dark:text-gray-400">{label}</dt>
              <dd className="min-w-0 truncate text-gray-900 dark:text-gray-50">{String(value)}</dd>
            </div>
          ))}
        </dl>
      </Section>

      <Section
        id="surveys"
        title="Surveys & assessments"
        count={surveys.length}
        action={plannedActions.survey || plannedActions.assessment}
      >
        {surveys.map((survey) => {
          const assessments = model.assessmentsBySurvey[survey.ref] || [];
          const questions = assessments.flatMap((a) => model.questionsByAssessment[a.ref] || []);
          const seen = new Set(questions.map((q) => q.ref));
          (model.questionsBySurvey[survey.ref] || []).forEach((q) => {
            if (!seen.has(q.ref)) questions.push(q);
          });
          const screens = model.screensBySurvey[survey.ref] || [];
          const scoring = assessments.flatMap((a) => model.scoringByAssessment[a.ref] || []);
          return (
            <div key={survey.ref} className="mt-2 first:mt-0">
              <button
                onClick={() => toggle(survey.ref)}
                className="-mx-2 flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left hover:bg-gray-50/80 dark:hover:bg-white/[0.03]"
              >
                <ChevronRight
                  className={`w-3.5 h-3.5 text-gray-400 shrink-0 transition-transform ${open.has(survey.ref) ? 'rotate-90' : ''}`}
                />
                <span className="flex-1 truncate text-[13px] text-gray-900 dark:text-gray-50">
                  {survey.name}
                </span>
                {survey.language && <Chip>{survey.language}</Chip>}
                {survey.survey_type && <Chip>{survey.survey_type}</Chip>}
                <Chip>{questions.length} questions</Chip>
                <Chip>{screens.length} screens</Chip>
              </button>
              {open.has(survey.ref) && (
                <div className="ml-6">
                  {assessments.map((a) => (
                    <p key={a.ref} className="text-xs text-gray-500 dark:text-gray-400 py-0.5">
                      {a.assessment_type || 'assessment'} v{a.version}
                      {a.cdisc_compliant ? ' · CDISC compliant' : ''}
                      {(survey.linked_surveys || []).length
                        ? ` · linked to ${(survey.linked_surveys || []).map((r) => refMap[r]?.name).filter(Boolean).join(', ')}`
                        : ''}
                    </p>
                  ))}
                  <SubSection id={`${survey.ref}/questions`} title="Questions" count={questions.length}>
                    {questions.map((q) => (
                      <Row key={q.ref} primary={q.text} chips={[q.type]} detail={q.code} />
                    ))}
                  </SubSection>
                  <SubSection id={`${survey.ref}/screens`} title="Screens" count={screens.length}>
                    {screens.map((s) => {
                      const sq = model.screenQuestionsByScreen[s.ref] || [];
                      const nav = model.navRulesByScreen[s.ref] || [];
                      return (
                        <Row
                          key={s.ref}
                          primary={s.name}
                          chips={[`${sq.length} q`, `${nav.length} rules`]}
                          detail={s.template ? `template: ${refMap[s.template]?.name}` : null}
                        />
                      );
                    })}
                  </SubSection>
                  {scoring.length > 0 && (
                    <SubSection id={`${survey.ref}/scoring`} title="Scoring rules" count={scoring.length}>
                      {scoring.map((r) => (
                        <Row key={r.ref} primary={r.name} chips={[r.scoring_type]} detail={r.description} />
                      ))}
                    </SubSection>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </Section>

      {templates.length > 0 && (
        <Section id="templates" title="Layout & assessment templates" count={templates.length} action={plannedActions.layout_template || plannedActions.assessment_template}>
          <ul className="divide-y divide-gray-100 dark:divide-white/[0.06]">
            {templates.map((t) => (
              <Row
                key={t.ref}
                primary={t.name}
                chips={[t._kind]}
                detail={t.assessment ? `for ${refMap[refMap[t.assessment]?.survey]?.name || 'assessment'}` : t.question_type}
              />
            ))}
          </ul>
        </Section>
      )}

      {variants.length > 0 && (
        <Section id="variants" title="Form variants (languages & devices)" count={variants.length} action={plannedActions.form_variant}>
          <ul className="divide-y divide-gray-100 dark:divide-white/[0.06]">
            {variants.map((v) => (
              <Row
                key={v.ref}
                primary={refMap[refMap[v.primary_assessment]?.survey]?.name || 'assessment'}
                chips={[v.language, refMap[v.device]?.name].filter(Boolean)}
                detail={v.is_translation_complete ? 'translation complete' : 'translation incomplete'}
              />
            ))}
          </ul>
        </Section>
      )}

      {schedules.length > 0 && (
        <Section id="schedules" title="Schedules" count={schedules.length} action={plannedActions.schedule}>
          {schedules.map((s) => {
            const versions = model.versionsBySchedule[s.ref] || [];
            const timepoints = (s.config?.timepoints || []).length;
            return (
              <div key={s.ref} className="py-1">
                <Row
                  primary={s.name}
                  chips={[`${timepoints} timepoints`, s.is_active ? 'active' : 'inactive']}
                  detail={s.description}
                />
                {versions.length > 0 && (
                  <SubSection id={`${s.ref}/versions`} title="Versions" count={versions.length}>
                    {versions.map((v) => (
                      <Row
                        key={v.ref}
                        primary={`v${v.version_number}${v.version_number === s.current_version_number ? ' (current)' : ''}`}
                        detail={v.reason}
                        chips={v.effective_from ? [String(v.effective_from).slice(0, 10)] : []}
                      />
                    ))}
                  </SubSection>
                )}
              </div>
            );
          })}
        </Section>
      )}

      {reminders.length > 0 && (
        <Section id="reminders" title="Reminder templates" count={reminders.length} action={plannedActions.reminder_template}>
          <ul className="divide-y divide-gray-100 dark:divide-white/[0.06]">
            {reminders.map((t) => (
              <Row
                key={t.ref}
                primary={t.name}
                chips={[t.channel, t.is_active ? 'active' : 'inactive'].filter(Boolean)}
                detail={t.template_content}
              />
            ))}
          </ul>
        </Section>
      )}

      {references.length > 0 && (
        <Section
          id="references"
          title="Reference data"
          count={references.length}
          action="match_existing"
          note="must already exist here; never created by the deploy"
        >
          <ul className="divide-y divide-gray-100 dark:divide-white/[0.06]">
            {references.map((r, i) => (
              <Row key={i} primary={r.label} chips={[r.kind, r.extra].filter(Boolean)} />
            ))}
          </ul>
        </Section>
      )}
    </div>
  );
};

export default PlannedChangesReview;
